import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ColorPicker } from 'primereact/colorpicker';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FinanceDocumentType } from '../types';
import { financeBookTypesAPI } from '../services/api';

type BookTypeFormState = {
  name: string;
  color: string; // hex e.g. #3b82f6
  icon: string; // e.g. pi-file
};

const normalizeIconClass = (icon?: string) => {
  if (!icon) return 'pi pi-file';
  return icon.includes(' ') ? icon : `pi ${icon}`;
};

const FinanceBookTypes: React.FC = () => {
  const [items, setItems] = useState<FinanceDocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<FinanceDocumentType | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<BookTypeFormState>({
    name: '',
    color: '#3b82f6',
    icon: 'pi-file'
  });

  const iconOptions = useMemo(
    () => [
      { label: 'Dokument', value: 'pi-file' },
      { label: 'Knjiga', value: 'pi-book' },
      { label: 'Folder', value: 'pi-folder' },
      { label: 'Izmena dokumenta', value: 'pi-file-edit' },
      { label: 'Inbox', value: 'pi-inbox' },
      { label: 'Kofer', value: 'pi-briefcase' },
    ],
    []
  );

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await financeBookTypesAPI.getAll();
      setItems(data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri učitavanju tipova registratora';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      color: '#3b82f6',
      icon: 'pi-file'
    });
    setError('');
    setDialogVisible(true);
  };

  const openEditDialog = (item: FinanceDocumentType) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      color: item.color || '#3b82f6',
      icon: item.icon?.includes(' ') ? item.icon.split(' ').slice(-1)[0] : (item.icon || 'pi-file')
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setError('Naziv je obavezan');

    try {
      setError('');
      if (editingItem) {
        await financeBookTypesAPI.update(editingItem._id, {
          name: formData.name,
          color: formData.color,
          icon: formData.icon
        });
      } else {
        await financeBookTypesAPI.create({
          name: formData.name,
          color: formData.color,
          icon: formData.icon
        });
      }
      setDialogVisible(false);
      await loadItems();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri čuvanju tipa registratora';
      setError(msg);
    }
  };

  const handleDelete = (item: FinanceDocumentType) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete tip registratora "${item.name}"?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await financeBookTypesAPI.delete(item._id);
          await loadItems();
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri brisanju tipa registratora';
          setError(msg);
        }
      }
    });
  };

  const iconBodyTemplate = (rowData: FinanceDocumentType) => {
    const bg = rowData.color || '#3b82f6';
    return (
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          backgroundColor: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <i className={normalizeIconClass(rowData.icon)} style={{ color: '#fff', fontSize: '1.1rem' }} />
      </div>
    );
  };

  const actionsBodyTemplate = (rowData: FinanceDocumentType) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => openEditDialog(rowData)}
          aria-label="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => handleDelete(rowData)}
          aria-label="Obriši"
        />
      </div>
    );
  };

  const iconValueTemplate = (option: any) => {
    if (!option) return null;
    return (
      <div className="flex align-items-center gap-2">
        <i className={normalizeIconClass(option.value)} />
        <span>{option.label}</span>
      </div>
    );
  };

  const iconItemTemplate = (option: any) => {
    return (
      <div className="flex align-items-center gap-2">
        <i className={normalizeIconClass(option.value)} />
        <span>{option.label}</span>
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Otkaži" className="p-button-text" onClick={() => setDialogVisible(false)} />
      <Button
        label={editingItem ? 'Sačuvaj' : 'Kreiraj'}
        className="p-button-success"
        onClick={handleSubmit}
      />
    </div>
  );

  return (
    <div className="p-4">
      <ConfirmDialog />

      <Card title="Tipovi registratora">
        <div className="flex justify-content-start mb-3">
          <Button
            label="Dodaj tip registratora"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={openCreateDialog}
          />
        </div>

        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          responsiveLayout="stack" breakpoint="768px"
          emptyMessage="Nema podataka"
        >
          <Column header="Ikona" body={iconBodyTemplate} style={{ width: '100px' }} />
          <Column field="name" header="Naziv" sortable />
          <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingItem ? 'Izmeni tip registratora' : 'Novi tip registratora'}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '95vw' }}
        onHide={() => setDialogVisible(false)}
        footer={dialogFooter}
        modal
      >
        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label htmlFor="bt-name">Naziv *</label>
            <InputText
              id="bt-name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="npr. Registrator A"
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="bt-color">Boja</label>
            <div className="flex align-items-center gap-3">
              <ColorPicker
                value={formData.color}
                onChange={(e) => setFormData((p) => ({ ...p, color: '#' + e.value }))}
              />
              <InputText
                id="bt-color"
                value={formData.color}
                onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))}
              />
            </div>
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="bt-icon">Ikona</label>
            <Dropdown
              id="bt-icon"
              value={formData.icon}
              options={iconOptions}
              onChange={(e) => setFormData((p) => ({ ...p, icon: e.value }))}
              appendTo="self"
              valueTemplate={iconValueTemplate}
              itemTemplate={iconItemTemplate}
              placeholder="Odaberi ikonu"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinanceBookTypes;


